import React, { useState, useEffect } from 'react';
import { getPackagesByType, PACKAGE_TYPES } from '../services/packageService';
import {
  createSubscription,
  getUserActiveSubscription,
  renewSubscription
} from '../services/subscriptionService';
import { initiateCashfreePayment, calculateGST, formatAmount } from '../services/cashfreeService';
import { createPaymentRecord, markPaymentSuccess, markPaymentFailed } from '../services/paymentService';
import { notifyAndroidPaymentSuccess } from '../services/androidBridge';
import LoadingSpinner from './LoadingSpinner';
import { useNavigate } from 'react-router-dom';

const PackageSelection = ({ user, userData, onSubscriptionUpdate }) => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);

  useEffect(() => {
    if (userData?.userType) {
      fetchPackages();
      fetchCurrentSubscription();
    }
  }, [userData]);

  // Add visibilitychange and focus listeners to check status when returning to the app
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && user && subscribing) {
        console.log('📱 App returned to foreground during checkout, checking status...');
        const result = await getUserActiveSubscription(user.uid);
        if (result.success && result.subscription) {
          console.log('✅ Found active subscription after returning to foreground:', result.subscription);
          setCurrentSubscription(result.subscription);
          if (onSubscriptionUpdate) {
            onSubscriptionUpdate(result.subscription);
          } else {
            window.location.href = '/dashboard';
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, [user, subscribing, onSubscriptionUpdate]);

  // Add interval to refresh subscription data, with fast polling during checkout
  useEffect(() => {
    if (!user?.uid) return;

    const intervalId = setInterval(async () => {
      if (subscribing) {
        // Fast poll (3s) during active purchase check
        const result = await getUserActiveSubscription(user.uid);
        if (result.success && result.subscription) {
          console.log('✅ Polling detected active subscription:', result.subscription);
          setCurrentSubscription(result.subscription);
          if (onSubscriptionUpdate) {
            onSubscriptionUpdate(result.subscription);
          } else {
            window.location.href = '/dashboard';
          }
        }
      } else {
        // Standard refresh (5s) when idle
        fetchCurrentSubscription();
      }
    }, subscribing ? 3000 : 5000);

    return () => clearInterval(intervalId);
  }, [user, subscribing, onSubscriptionUpdate]);

  const fetchPackages = async () => {
    if (!userData?.userType) {
      console.warn('❌ No user type found, cannot fetch packages');
      setLoading(false);
      return;
    }

    // Explicit mapping for user types to package types
    let packageType;
    if (userData.userType === 'jobseeker') {
      packageType = PACKAGE_TYPES.JOB_SEEKER; // 'job_seeker'
      console.log('👤 User is JOB SEEKER, fetching job_seeker packages');
    } else if (userData.userType === 'employer') {
      packageType = PACKAGE_TYPES.EMPLOYER; // 'employer'
      console.log('🏢 User is EMPLOYER, fetching employer packages');
    } else {
      console.warn('⚠️ Unknown user type:', userData.userType);
      setLoading(false);
      return;
    }

    console.log(`🔍 Fetching packages for ${userData.userType} (package type: ${packageType})`);

    const result = await getPackagesByType(packageType);

    if (result.success) {
      console.log(`✅ Found ${result.packages.length} ${packageType} packages`);
      console.log('📦 Packages:', result.packages.map(p => ({ name: p.name, type: p.type })));
      setPackages(result.packages);
    } else {
      console.error('❌ Failed to fetch packages:', result.error);
      // Fallback: try to get all packages and filter client-side
      try {
        const { getAllPackages } = await import('../services/packageService');
        const allPackagesResult = await getAllPackages();
        if (allPackagesResult.success) {
          const filteredPackages = allPackagesResult.packages.filter(
            pkg => pkg.type === packageType && pkg.status === 'active'
          );
          console.log(`🔄 Fallback: Found ${filteredPackages.length} ${packageType} packages`);
          console.log('📦 Filtered packages:', filteredPackages.map(p => ({ name: p.name, type: p.type })));
          setPackages(filteredPackages);
        }
      } catch (fallbackError) {
        console.error('❌ Fallback package fetch failed:', fallbackError);
      }
    }
    setLoading(false);
  };

  const fetchCurrentSubscription = async () => {
    if (!user?.uid) return;

    const result = await getUserActiveSubscription(user.uid);
    if (result.success && result.subscription) {
      setCurrentSubscription(result.subscription);
    }
  };

  const handleSubscribe = async (pkg) => {
    if (!user?.uid) {
      alert('Please login to purchase a package');
      return;
    }

    setSubscribing(pkg.id);

    try {
      console.log('🛒 [PackageSelection] Starting purchase for:', pkg.name);

      // Calculate GST (18%)
      const gstCalculation = calculateGST(pkg.price);
      const baseAmount = gstCalculation.baseAmount;
      const gstAmount = gstCalculation.gstAmount;
      const totalAmount = gstCalculation.totalAmount;

      console.log('💰 [PackageSelection] Price breakdown:', {
        baseAmount,
        gstAmount,
        totalAmount,
        gstRate: '18%'
      });

      // Create payment record first
      const paymentData = {
        userId: user.uid,
        packageId: pkg.id,
        packageName: pkg.name,
        amount: baseAmount,
        currency: 'INR',
        gstAmount: gstAmount,
        totalAmount: totalAmount,
        userEmail: userData?.email || user.email,
        userName: userData?.firstName ? `${userData.firstName} ${userData.lastName || ''}`.trim() : user.displayName,
        userPhone: userData?.phone || '',
        metadata: {
          packageType: pkg.type,
          duration: pkg.duration,
          maxApplications: pkg.maxApplications,
          gstRate: 18,
        },
      };

      const paymentResult = await createPaymentRecord(paymentData);

      if (!paymentResult.success) {
        throw new Error('Failed to create payment record');
      }

      const firebasePaymentId = paymentResult.paymentId;
      console.log('✅ [PackageSelection] Payment record created:', firebasePaymentId);

      // Initiate Cashfree payment with total amount (including GST)
      await initiateCashfreePayment({
        amount: totalAmount,
        baseAmount: baseAmount,
        gstAmount: gstAmount,
        currency: 'INR',
        packageName: pkg.name,
        packageId: pkg.id,
        userEmail: userData?.email || user.email,
        userName: userData?.firstName ? `${userData.firstName} ${userData.lastName || ''}`.trim() : user.displayName,
        userPhone: userData?.phone || '',
        onSuccess: async (paymentResponse) => {
          console.log('✅ [PackageSelection] Payment successful:', paymentResponse);

          // Update payment record
          await markPaymentSuccess(firebasePaymentId, paymentResponse);

          // Create subscription
          const subscriptionData = {
            userId: user.uid,
            packageId: pkg.id,
            packageName: pkg.name,
            packageType: pkg.type,
            price: pkg.price,
            duration: pkg.duration,
            features: pkg.features || [],
            maxApplications: pkg.maxApplications || 0,
            maxJobPostings: pkg.maxJobPostings || 0,
            featuredJobs: pkg.featuredJobs || 0,
            resumeTemplates: pkg.resumeTemplates || 0,
            profileBoost: pkg.profileBoost || false,
            candidateSearch: pkg.candidateSearch || false,
            analyticsAccess: pkg.analyticsAccess || false,
            userType: userData.userType,
            paymentId: firebasePaymentId,
            cashfreePaymentId: paymentResponse.paymentId,
            cashfreeOrderId: paymentResponse.orderId || null,
          };

          const subscriptionResult = await createSubscription(subscriptionData);

          if (subscriptionResult.success) {
            notifyAndroidPaymentSuccess(pkg.name);
            fetchCurrentSubscription();
            if (onSubscriptionUpdate) {
              onSubscriptionUpdate(subscriptionResult.subscription);
            } else {
              window.location.href = '/dashboard';
            }
          } else {
            alert('Payment successful but failed to activate subscription. Please contact support.');
          }

          setSubscribing(null);
        },
        onFailure: async (error) => {
          console.error('❌ [PackageSelection] Payment failed:', error);

          // Update payment record
          await markPaymentFailed(firebasePaymentId, error.error || 'Payment failed');

          if (!error.cancelled) {
            alert('Payment failed: ' + (error.error || 'Unknown error'));
          }

          setSubscribing(null);
        },
      });

    } catch (error) {
      console.error('❌ [PackageSelection] Error:', error);
      alert('Error: ' + error.message);
      setSubscribing(null);
    }
  };

  const isCurrentPackage = (pkg) => {
    return currentSubscription && currentSubscription.packageId === pkg.id;
  };

  const getSubscriptionStatus = () => {
    if (!currentSubscription) return null;

    const endDate = currentSubscription.endDate.toDate();
    const now = new Date();
    const daysLeft = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));

    return {
      packageName: currentSubscription.packageName,
      daysLeft,
      isExpiring: daysLeft <= 7
    };
  };

  if (loading) return <LoadingSpinner />;

  const subscriptionStatus = getSubscriptionStatus();

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Choose Your Package</h1>
        <p className="text-gray-600">
          Select a package that best fits your {userData?.userType === 'jobseeker' ? 'job search' : 'hiring'} needs
        </p>

        {/* User Type Indicator */}
        <div className="mt-4 inline-flex items-center px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${userData?.userType === 'jobseeker' ? 'bg-blue-500' : 'bg-purple-500'}`}></div>
            <span className="text-sm font-medium text-gray-700">
              Showing packages for: <span className="font-semibold text-gray-900">
                {userData?.userType === 'jobseeker' ? 'Job Seekers (Employees)' : 'Employers (Speakers)'}
              </span>
            </span>
          </div>
        </div>
      </div>

      {/* Current Subscription Status */}
      {subscriptionStatus && currentSubscription && (
        <div className={`mb-6 rounded-2xl border-2 shadow-xl overflow-hidden ${subscriptionStatus.isExpiring
          ? 'bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-300'
          : 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-300'
          }`}>
          <div className="p-6">
            {/* Header with Refresh Button */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${subscriptionStatus.isExpiring ? 'bg-yellow-500' : 'bg-green-500'
                  }`}>
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className={`text-xl font-bold ${subscriptionStatus.isExpiring ? 'text-yellow-900' : 'text-green-900'
                    }`}>
                    {subscriptionStatus.packageName}
                  </h3>
                  <p className={`text-sm font-medium ${subscriptionStatus.isExpiring ? 'text-yellow-700' : 'text-green-700'
                    }`}>
                    Active Subscription
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {/* Refresh Button */}
                <button
                  onClick={() => fetchCurrentSubscription()}
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700 shadow-sm"
                  title="Refresh usage data"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Refresh
                </button>

                {subscriptionStatus.isExpiring && (
                  <div className="flex items-center gap-2 bg-yellow-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    Expiring Soon
                  </div>
                )}
              </div>
            </div>

            {/* Package Benefits & Usage Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column - Usage Stats */}
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900 text-sm uppercase tracking-wide">Usage Statistics</h4>

                {/* Job Applications */}
                {userData?.userType === 'jobseeker' && currentSubscription.maxApplications && (
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span className="font-medium text-gray-900">Job Applications</span>
                      </div>
                      <span className={`text-sm font-bold px-3 py-1 rounded-full ${(currentSubscription.usageStats?.applicationsUsed || 0) >= currentSubscription.maxApplications
                        ? 'bg-red-100 text-red-700'
                        : (currentSubscription.usageStats?.applicationsUsed || 0) >= currentSubscription.maxApplications * 0.8
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-green-100 text-green-700'
                        }`}>
                        {currentSubscription.usageStats?.applicationsUsed || 0} / {currentSubscription.maxApplications}
                      </span>
                    </div>

                    {/* Progress Bar */}
                    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${(currentSubscription.usageStats?.applicationsUsed || 0) >= currentSubscription.maxApplications
                          ? 'bg-gradient-to-r from-red-500 to-red-600'
                          : (currentSubscription.usageStats?.applicationsUsed || 0) >= currentSubscription.maxApplications * 0.8
                            ? 'bg-gradient-to-r from-yellow-500 to-orange-500'
                            : 'bg-gradient-to-r from-green-500 to-emerald-600'
                          }`}
                        style={{
                          width: `${Math.min(((currentSubscription.usageStats?.applicationsUsed || 0) / currentSubscription.maxApplications) * 100, 100)}%`
                        }}
                      ></div>
                    </div>

                    <p className="text-xs text-gray-600 mt-2">
                      {currentSubscription.maxApplications - (currentSubscription.usageStats?.applicationsUsed || 0)} applications remaining
                    </p>
                  </div>
                )}

                {/* Job Postings for Employers */}
                {userData?.userType === 'employer' && currentSubscription.maxJobPostings && (
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m8 0h-8m8 0v6m-8-6v6m8 0h-8" />
                        </svg>
                        <span className="font-medium text-gray-900">Job Postings</span>
                      </div>
                      <span className={`text-sm font-bold px-3 py-1 rounded-full ${(currentSubscription.usageStats?.jobPostingsUsed || 0) >= currentSubscription.maxJobPostings
                        ? 'bg-red-100 text-red-700'
                        : 'bg-green-100 text-green-700'
                        }`}>
                        {currentSubscription.usageStats?.jobPostingsUsed || 0} / {currentSubscription.maxJobPostings}
                      </span>
                    </div>

                    {/* Progress Bar */}
                    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${(currentSubscription.usageStats?.jobPostingsUsed || 0) >= currentSubscription.maxJobPostings
                          ? 'bg-gradient-to-r from-red-500 to-red-600'
                          : 'bg-gradient-to-r from-purple-500 to-indigo-600'
                          }`}
                        style={{
                          width: `${Math.min(((currentSubscription.usageStats?.jobPostingsUsed || 0) / currentSubscription.maxJobPostings) * 100, 100)}%`
                        }}
                      ></div>
                    </div>

                    <p className="text-xs text-gray-600 mt-2">
                      {currentSubscription.maxJobPostings - (currentSubscription.usageStats?.jobPostingsUsed || 0)} postings remaining
                    </p>
                  </div>
                )}
              </div>

              {/* Right Column - Package Benefits */}
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900 text-sm uppercase tracking-wide">Package Benefits</h4>

                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 space-y-3">
                  {/* Validity */}
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">Validity</p>
                      <p className={`text-xs font-semibold ${subscriptionStatus.isExpiring ? 'text-yellow-700' : 'text-green-700'
                        }`}>
                        {subscriptionStatus.daysLeft > 0
                          ? `${subscriptionStatus.daysLeft} days remaining`
                          : 'Expired'
                        }
                      </p>
                    </div>
                  </div>

                  {/* Additional Benefits */}
                  {userData?.userType === 'jobseeker' && (
                    <>
                      {currentSubscription.resumeTemplates > 0 && (
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">Resume Templates</p>
                            <p className="text-xs text-gray-600">{currentSubscription.resumeTemplates} templates</p>
                          </div>
                        </div>
                      )}
                      {currentSubscription.profileBoost && (
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">Profile Boost</p>
                            <p className="text-xs text-gray-600">Enhanced visibility</p>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {userData?.userType === 'employer' && (
                    <>
                      {currentSubscription.featuredJobs > 0 && (
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">Featured Jobs</p>
                            <p className="text-xs text-gray-600">{currentSubscription.featuredJobs} featured slots</p>
                          </div>
                        </div>
                      )}
                      {currentSubscription.candidateSearch && (
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">Candidate Search</p>
                            <p className="text-xs text-gray-600">Advanced search access</p>
                          </div>
                        </div>
                      )}
                      {currentSubscription.analyticsAccess && (
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-pink-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">Analytics</p>
                            <p className="text-xs text-gray-600">Detailed reports</p>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Package Cards - Modern Pricing Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {packages.map((pkg, index) => (
          <div
            key={pkg.id}
            className={`relative bg-gradient-to-br from-white via-blue-50/30 to-purple-50/30 rounded-2xl shadow-xl border transition-all duration-300 hover:shadow-2xl hover:scale-105 ${isCurrentPackage(pkg)
              ? 'border-green-500 ring-4 ring-green-100'
              : index === 1
                ? 'border-indigo-500 ring-4 ring-indigo-100 transform scale-105'
                : 'border-gray-200/50 hover:border-indigo-300'
              }`}
          >
            {/* Popular Badge */}
            {index === 1 && (
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                  RECOMMENDED
                </span>
              </div>
            )}

            {/* Current Package Badge */}
            {isCurrentPackage(pkg) && (
              <div className="absolute -top-4 right-4 z-10">
                <span className="bg-green-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                  CURRENT
                </span>
              </div>
            )}

            <div className="p-8">
              {/* Package Header */}
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{pkg.name}</h3>
                {/* Price Breakdown */}
                <div className="mb-4">
                  <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-4 mb-3">
                    <div className="flex justify-between items-center text-sm text-gray-600 mb-2">
                      <span>Base Price:</span>
                      <span className="font-semibold">{formatAmount(pkg.price)}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm text-gray-600 mb-2">
                      <span>GST (18%):</span>
                      <span className="font-semibold">{formatAmount(calculateGST(pkg.price).gstAmount)}</span>
                    </div>
                    <div className="border-t border-gray-300 pt-2 mt-2">
                      <div className="flex justify-between items-center">
                        <span className="text-base font-bold text-gray-900">Total Amount:</span>
                        <span className="text-3xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                          {formatAmount(calculateGST(pkg.price).totalAmount)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <span className="text-gray-500 text-sm">
                    Valid for {pkg.duration} days
                  </span>
                </div>
                {/* Description as formatted text */}
                {pkg.description && (
                  <div className="text-gray-600 leading-relaxed text-center max-w-sm mx-auto">
                    <p className="text-sm">{pkg.description}</p>
                  </div>
                )}
              </div>

              {/* Key Features Display */}
              <div className="mb-8">
                <div className="space-y-3">
                  {/* Dynamic Features based on package type */}
                  {userData?.userType === 'jobseeker' ? (
                    <>
                      {pkg.maxApplications && (
                        <div className="flex items-center justify-center">
                          <div className="flex items-center bg-indigo-50 px-4 py-2 rounded-full">
                            <svg className="w-4 h-4 text-indigo-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            <span className="text-indigo-700 font-medium text-sm">{pkg.maxApplications} Job Applications</span>
                          </div>
                        </div>
                      )}
                      {pkg.resumeTemplates && (
                        <div className="flex items-center justify-center">
                          <div className="flex items-center bg-indigo-50 px-4 py-2 rounded-full">
                            <svg className="w-4 h-4 text-indigo-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            <span className="text-indigo-700 font-medium text-sm">{pkg.resumeTemplates} Resume Templates</span>
                          </div>
                        </div>
                      )}
                      {pkg.profileBoost && (
                        <div className="flex items-center justify-center">
                          <div className="flex items-center bg-indigo-50 px-4 py-2 rounded-full">
                            <svg className="w-4 h-4 text-indigo-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            <span className="text-indigo-700 font-medium text-sm">Profile Boost</span>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {pkg.maxJobPostings && (
                        <div className="flex items-center justify-center">
                          <div className="flex items-center bg-indigo-50 px-4 py-2 rounded-full">
                            <svg className="w-4 h-4 text-indigo-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            <span className="text-indigo-700 font-medium text-sm">{pkg.maxJobPostings} Job Postings</span>
                          </div>
                        </div>
                      )}
                      {pkg.featuredJobs && (
                        <div className="flex items-center justify-center">
                          <div className="flex items-center bg-indigo-50 px-4 py-2 rounded-full">
                            <svg className="w-4 h-4 text-indigo-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            <span className="text-indigo-700 font-medium text-sm">{pkg.featuredJobs} Featured Jobs</span>
                          </div>
                        </div>
                      )}
                      {pkg.candidateSearch && (
                        <div className="flex items-center justify-center">
                          <div className="flex items-center bg-indigo-50 px-4 py-2 rounded-full">
                            <svg className="w-4 h-4 text-indigo-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            <span className="text-indigo-700 font-medium text-sm">Advanced Candidate Search</span>
                          </div>
                        </div>
                      )}
                      {pkg.analyticsAccess && (
                        <div className="flex items-center justify-center">
                          <div className="flex items-center bg-indigo-50 px-4 py-2 rounded-full">
                            <svg className="w-4 h-4 text-indigo-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            <span className="text-indigo-700 font-medium text-sm">Analytics & Reports</span>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Additional Features */}
                  {pkg.features && pkg.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center justify-center">
                      <div className="flex items-center bg-indigo-50 px-4 py-2 rounded-full">
                        <svg className="w-4 h-4 text-indigo-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="text-indigo-700 font-medium text-sm">{feature}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Button */}
              <button
                onClick={() => handleSubscribe(pkg)}
                disabled={subscribing === pkg.id}
                className={`w-full py-4 px-6 rounded-xl font-bold text-lg transition-all duration-300 transform hover:scale-105 ${isCurrentPackage(pkg)
                  ? 'bg-green-500 text-white cursor-default shadow-lg'
                  : subscribing === pkg.id
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : index === 1
                      ? 'bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white hover:from-indigo-700 hover:via-purple-700 hover:to-pink-700 shadow-xl'
                      : 'bg-gradient-to-r from-gray-800 to-gray-900 text-white hover:from-gray-700 hover:to-gray-800 shadow-lg'
                  }`}
              >
                {subscribing === pkg.id
                  ? 'Processing...'
                  : isCurrentPackage(pkg)
                    ? 'Current Package'
                    : currentSubscription
                      ? `Upgrade to ${pkg.name}`
                      : `Get ${pkg.name}`
                }
              </button>
            </div>
          </div>
        ))}
      </div>

      {packages.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">📦</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No packages available</h3>
          <p className="text-gray-500 mb-4">
            No packages are currently available for {userData?.userType === 'jobseeker' ? 'employees (job seekers)' : 'speakers (employers)'}.
          </p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 max-w-md mx-auto">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> Packages are filtered by user type:
            </p>
            <ul className="text-sm text-yellow-700 mt-2 text-left">
              <li>• <strong>Employees (Job Seekers)</strong> see job seeker packages</li>
              <li>• <strong>Speakers (Employers)</strong> see employer packages</li>
            </ul>
            <p className="text-sm text-yellow-700 mt-2">
              If you're a superadmin, please create packages with the correct type for this user category.
            </p>
          </div>
        </div>
      )}

      {/* Free Usage Notice */}
      {!currentSubscription && (
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <div className="text-blue-500 mr-3 mt-1">ℹ️</div>
            <div>
              <h4 className="font-semibold text-blue-900 mb-1">Free Usage</h4>
              <p className="text-blue-700 text-sm">
                You're currently using the free tier with limited features.
                Subscribe to a package to unlock full functionality and remove restrictions.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PackageSelection;
